/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link } from "../components/ui/Link";
import { ChevronRight, Inbox as InboxIcon, MessageSquareText } from "lucide-react";
import { listOpen, type CoordinatorCase } from "../api/queue";
import { useAuth } from "../auth/AuthContext";
import { useChrome } from "../contexts/ChromeContext";
import { EmptyState } from "../components/ui/EmptyState";
import { ConfidenceRing } from "../components/ui/ConfidenceRing";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Spinner } from "../components/ui/Spinner";
import { formatSlot } from "../lib/runs";

const POLL_MILLIS = 30_000;

function titleCase(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase();
}

function partOfDay(): string {
  const hour = new Date().getHours();
  if (hour < 12) {
    return "Good morning";
  }
  if (hour < 18) {
    return "Good afternoon";
  }
  return "Good evening";
}

function firstName(email: string): string {
  const local = email.split("@")[0] ?? "";
  const token = local.split(/[._+-]/)[0] ?? local;
  return token === "" ? "there" : token.charAt(0).toUpperCase() + token.slice(1);
}

function subline(count: number): string {
  if (count === 0) {
    return "Nothing needs you right now. The agent has the rest in motion.";
  }
  if (count === 1) {
    return "One case is waiting on you. Everything else is in motion.";
  }
  return `${count} cases are waiting on you. Everything else is in motion.`;
}

function claimedSubline(count: number): string {
  if (count === 0) {
    return "No cases are claimed right now.";
  }
  if (count === 1) {
    return "One case is being worked by the team.";
  }
  return `${count} cases are being worked by the team.`;
}

export function Inbox({ view = "open" }: { view?: "open" | "claimed" }) {
  const { session } = useAuth();
  const [cases, setCases] = useState<CoordinatorCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const result = await listOpen();
    if (result.ok) {
      setCases(result.value);
      setError(null);
    } else {
      setError(result.error.message);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_MILLIS);
    return () => clearInterval(timer);
  }, [refresh]);

  const greeting = useMemo(() => partOfDay(), []);
  const name = useMemo(
    () => (session.state === "authenticated" ? firstName(session.user.email) : "there"),
    [session],
  );

  const visible =
    view === "claimed"
      ? (cases?.filter((c) => c.entry.status === "CLAIMED") ?? null)
      : cases;
  const count = visible?.length ?? 0;

  useChrome(
    {
      title: view === "claimed" ? "Claimed" : "Inbox",
    },
    [view],
  );

  if (cases === null && error === null) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="enter max-w-2xl">
        {view === "claimed" ? (
          <>
            <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
              Claimed
            </h1>
            <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-text-secondary">
              {claimedSubline(count)}
            </p>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
              {greeting}, <em className="italic">{name}</em>.
            </h1>
            <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-text-secondary">
              {subline(count)}
            </p>
          </>
        )}
      </header>

      <ErrorMessage message={error} className="mb-0" />

      {count === 0 ? (
        <EmptyState
          icon={<InboxIcon size={20} />}
          title={view === "claimed" ? "Nothing claimed yet" : "The inbox is clear"}
          description={
            view === "claimed"
              ? "Cases a coordinator picks up show here."
              : "When the scheduling agent isn't sure, its case lands here."
          }
        />
      ) : (
        <ol className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visible?.map((c, index) => (
            <QueueCard key={c.entry.id} c={c} index={index} />
          ))}
        </ol>
      )}
    </div>
  );
}

function QueueCard({ c, index }: { c: CoordinatorCase; index: number }) {
  const { entry, patient, waitlist, outreach } = c;
  const providerName = [c.provider?.firstName, c.provider?.lastName]
    .filter((part) => part != null)
    .join(" ");
  const name =
    entry.patientId === null
      ? providerName !== ""
        ? `${providerName}'s freed slot`
        : "Open slot"
      : [patient.firstName, patient.lastName].filter((p) => p !== null).join(" ") ||
        "Unknown family";
  const claimed = entry.status === "CLAIMED";
  const slot =
    entry.freedSlotStartsAt !== null
      ? formatSlot(entry.freedSlotStartsAt, entry.freedSlotEndsAt)
      : null;
  const context = [
    waitlist?.services[0] !== undefined ? titleCase(waitlist.services[0].replaceAll("_", " ")) : null,
    waitlist?.acuity != null ? `${titleCase(waitlist.acuity)} acuity` : null,
  ]
    .filter((part): part is string => part !== null)
    .join("  ·  ");

  return (
    <li>
      <Link
        to={`/streams/${entry.agentRunId}`}
        className="group border border-border bg-bg-elevated shadow-card lift enter flex h-full flex-col gap-4 rounded-3xl p-5 sm:p-6"
        style={{ "--enter-delay": `${Math.min(index, 8) * 55 + 70}ms` } as CSSProperties}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="font-display text-2xl leading-tight text-text-primary">{name}</h2>
            {context !== "" && <p className="mt-0.5 truncate text-xs text-text-tertiary">{context}</p>}
          </div>
          {entry.confidence !== null && <ConfidenceRing value={entry.confidence} />}
        </div>

        {outreach !== null && outreach.responseBody !== null && (
          <div>
            <div className="flex items-center gap-1.5 text-[11px] text-text-tertiary">
              <MessageSquareText size={11} />
              {patient.firstName ?? "the family"} replied by text
            </div>
            <p className="mt-1.5 line-clamp-2 rounded-2xl rounded-tl-sm bg-bg-hover px-3.5 py-2 text-sm leading-relaxed text-text-primary ring-1 ring-border">
              {outreach.responseBody}
            </p>
          </div>
        )}

        <blockquote className="line-clamp-2 border-l-2 border-accent/40 pl-3 text-sm leading-relaxed text-text-secondary">
          {entry.reasoning}
        </blockquote>

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-border/60 pt-3 text-xs">
          <div className="flex items-center gap-2 text-text-tertiary">
            {claimed && (
              <span className="rounded-md bg-bg-hover px-1.5 py-0.5 font-medium text-text-secondary">
                claimed
              </span>
            )}
            {slot !== null && <span>{slot}</span>}
          </div>
          <span className="inline-flex items-center gap-1 font-medium text-text-tertiary transition-colors group-hover:text-text-primary">
            Open
            <ChevronRight
              size={14}
              className="transition-transform duration-200 group-hover:translate-x-1"
            />
          </span>
        </div>
      </Link>
    </li>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="px-5 py-4">
      <dd className="font-display text-[1.875rem] leading-none text-text-primary">{value}</dd>
      <dt className="mt-1.5 text-[11px] uppercase tracking-[0.1em] text-text-tertiary">{label}</dt>
    </div>
  );
}
